import "@/App.css";
import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SalesIQChat from "@/components/SalesIQChat";
import ScrollToTop from "@/components/ScrollToTop";
import NoIndex from "@/components/NoIndex";

const funnelRoutes = ["/annual-plans", "/monthly-plans", "/compare", "/start", "/gym", "/partner", "/partners", "/partners/one-pager"];

const LEGACY_NOINDEX_ROUTES = [
  "/annual-plans",
  "/monthly-plans",
  "/compare",
  "/pricing",
  "/legup",
  "/dosevana",
  "/glp1",
  "/programs/semaglutide",
  "/programs/tirzepatide"
];

const HomePage = lazy(() => import("@/pages/HomePage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const GLP1LandingPage = lazy(() => import("@/pages/GLP1LandingPage"));
const SemaglutideProgramPage = lazy(() => import("@/pages/SemaglutideProgramPage"));
const TirzepatideProgramPage = lazy(() => import("@/pages/TirzepatideProgramPage"));
const HGHInfoPage = lazy(() => import("@/pages/HGHInfoPage"));
const DosevanaLandingPage = lazy(() => import("@/pages/DosevanaLandingPage"));
const StartIntakePage = lazy(() => import("@/pages/StartIntakePage"));
const DosevanaCatalogPage = lazy(() => import("@/pages/DosevanaCatalogPage"));
const DosevanaIntakePage = lazy(() => import("@/pages/DosevanaIntakePage"));
const LegUpLandingPage = lazy(() => import("@/pages/LegUpLandingPage"));
const FindYourPathPage = lazy(() => import("@/pages/FindYourPathPage"));
const ProvidersPage = lazy(() => import("@/pages/ProvidersPage"));
const PlanPage = lazy(() => import("@/pages/PlanPage"));
const CheckoutPage = lazy(() => import("@/pages/CheckoutPage"));
const PayPage = lazy(() => import("@/pages/PayPage"));
const StartPage = lazy(() => import("@/pages/StartPage"));
const SpecialtyReviewPage = lazy(() => import("@/pages/SpecialtyReviewPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const AdminPartnersPage = lazy(() => import("@/pages/AdminPartnersPage"));
const GymPartnerFunnelPage = lazy(() => import("@/pages/GymPartnerFunnelPage"));
const PartnerOpportunityPage = lazy(() => import("@/pages/PartnerOpportunityPage"));
const PartnerDashboardPage = lazy(() => import("@/pages/PartnerDashboardPage"));
const PartnerLoginPage = lazy(() => import("@/pages/PartnerLoginPage"));
const PartnerSetupPage = lazy(() => import("@/pages/PartnerSetupPage"));
const PartnerOnePagerPage = lazy(() => import("@/pages/PartnerOnePagerPage"));
const RefundPolicyPage = lazy(() => import("@/pages/RefundPolicyPage"));
const SwitchToRxPage = lazy(() => import("@/pages/SwitchToRxPage"));
const AdvancedTherapyPage = lazy(() => import("@/pages/AdvancedTherapyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));

const AppLayout = () => {
  const location = useLocation();
  const isFunnelRoute = funnelRoutes.some((route) => location.pathname.startsWith(route));
  const shouldNoIndex = LEGACY_NOINDEX_ROUTES.some((route) => location.pathname.startsWith(route));

  return (
    <div className="App min-h-screen flex flex-col">
      {shouldNoIndex && <NoIndex />}
      <ScrollToTop />
      {!isFunnelRoute && <Navbar />}
      <main className="flex-1">
        <Suspense
          fallback={
            <div className="flex items-center justify-center py-16 text-sm text-[#718096]" data-testid="route-loading">
              Loading...
            </div>
          }
        >
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/pricing" element={<Navigate to="/plan" replace />} />
            <Route path="/annual-plans" element={<Navigate to="/plan" replace />} />
            <Route path="/monthly-plans" element={<Navigate to="/plan" replace />} />
            <Route path="/compare" element={<Navigate to="/plan" replace />} />
            <Route path="/providers" element={<ProvidersPage />} />
            <Route path="/find-your-path" element={<FindYourPathPage />} />
            <Route path="/glp1" element={<GLP1LandingPage />} />
            <Route path="/programs/semaglutide" element={<Navigate to="/plan" replace />} />
            <Route path="/programs/tirzepatide" element={<Navigate to="/plan" replace />} />
            <Route path="/hgh-info" element={<HGHInfoPage />} />
            <Route path="/dosevana" element={<DosevanaLandingPage />} />
            <Route path="/start-intake" element={<StartIntakePage />} />
            <Route path="/dosevana-catalog" element={<DosevanaCatalogPage />} />
            <Route path="/dosevana-intake" element={<DosevanaIntakePage />} />
            <Route path="/legup" element={<LegUpLandingPage />} />
            <Route path="/plan" element={<PlanPage />} />
            <Route path="/switch-to-rx" element={<SwitchToRxPage />} />
            <Route path="/advanced-therapy" element={<AdvancedTherapyPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/pay" element={<PayPage />} />
            <Route path="/start" element={<StartPage />} />
            <Route path="/specialty-review" element={<Navigate to="/plan" replace />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/admin/partners" element={<AdminPartnersPage />} />
            <Route path="/gym" element={<GymPartnerFunnelPage />} />
            <Route path="/partners" element={<PartnerOpportunityPage />} />
            <Route path="/affiliates" element={<PartnerOpportunityPage />} />
            <Route path="/partners/one-pager" element={<PartnerOnePagerPage />} />
            <Route path="/partner/login" element={<PartnerLoginPage />} />
            <Route path="/partner/setup" element={<PartnerSetupPage />} />
            <Route path="/partner/setup/:token" element={<PartnerSetupPage />} />
            <Route path="/partner/:slug" element={<PartnerDashboardPage />} />
            <Route path="/refund-policy" element={<RefundPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
      <Toaster position="bottom-right" />
      <SalesIQChat />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AppLayout />
    </BrowserRouter>
  );
}

export default App;
